import { forwardRef } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/utils';

export type InputSize = 'sm' | 'md' | 'lg';

const SIZES: Record<InputSize, string> = {
  sm: 'h-8 text-[13px]',
  md: 'h-9 text-sm',
  lg: 'h-11 text-[15px]',
};

export const INPUT_BASE = cn(
  'w-full rounded-md border border-border bg-surface text-fg',
  'transition-[border-color,box-shadow,background-color] duration-150 placeholder:text-subtle',
  'focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/35',
  'disabled:cursor-not-allowed disabled:bg-elevated disabled:opacity-60',
);

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
  /** Control height. Named `inputSize` so the native `size` attribute still works. */
  inputSize?: InputSize;
  /** Icon or short adornment rendered inside the field, on the left. */
  leadingIcon?: ReactNode;
  /** Icon, unit or action rendered inside the field, on the right. */
  trailingIcon?: ReactNode;
  /** Applies to the wrapper when adornments are present. */
  wrapperClassName?: string;
}

/**
 * Text field. Without adornments it renders a bare `<input>` so callers can size
 * it with `className` exactly as they expect; with a leading/trailing icon it
 * wraps in a relative container and reserves the padding.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid, inputSize = 'md', leadingIcon, trailingIcon, wrapperClassName, ...props },
  ref,
) {
  const control = (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        INPUT_BASE,
        SIZES[inputSize],
        'px-3',
        leadingIcon && 'pl-9',
        trailingIcon && 'pr-9',
        invalid && 'border-danger focus-visible:border-danger focus-visible:ring-danger/30',
        className,
      )}
      {...props}
    />
  );

  if (!leadingIcon && !trailingIcon) return control;

  return (
    <div className={cn('relative w-full', wrapperClassName)}>
      {leadingIcon && (
        <span
          className="pointer-events-none absolute left-3 top-1/2 flex -translate-y-1/2 items-center text-subtle [&_svg]:size-4"
          aria-hidden
        >
          {leadingIcon}
        </span>
      )}
      {control}
      {trailingIcon && (
        <span className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center text-subtle [&_svg]:size-4">
          {trailingIcon}
        </span>
      )}
    </div>
  );
});
